import { POPULAR_PRESET } from '../constants/pricing';

export const buildTariffKey = (tariff) => (
  `${tariff.duration_days}-${tariff.device_limit}-${tariff.traffic_gb}`
);

export const formatTraffic = (gb) => {
  if (!gb || gb === 0) return '∞';
  return `${gb} ГБ`;
};

export const formatDevices = (limit) => {
  if (!limit || limit === 0) return '∞';
  return limit;
};

const isPaidTariff = (tariff) => tariff.price > 0 && tariff.duration_days > 0;

const findBestByPricePerDay = (tariffs) => (
  tariffs.reduce((best, tariff) => {
    const pricePerDay = tariff.price / tariff.duration_days;

    if (!best) {
      return { tariff, pricePerDay };
    }

    if (pricePerDay < best.pricePerDay) {
      return { tariff, pricePerDay };
    }

    if (pricePerDay === best.pricePerDay && tariff.duration_days > best.tariff.duration_days) {
      return { tariff, pricePerDay };
    }

    return best;
  }, null)
);

export const buildPricingCards = (tariffs) => {
  if (!Array.isArray(tariffs) || tariffs.length === 0) {
    return {
      orderedTariffs: [],
      cheapestKey: null,
      popularKey: null,
      bestKey: null,
      maxPricePerDay: 0,
    };
  }

  const sortedTariffs = [...tariffs].sort((a, b) => a.price - b.price);

  const cheapestTariff = sortedTariffs[0] || null;
  const cheapestKey = cheapestTariff ? buildTariffKey(cheapestTariff) : null;

  const popularTariff = sortedTariffs.find((tariff) => (
    tariff.duration_days === POPULAR_PRESET.duration_days
    && tariff.device_limit === POPULAR_PRESET.device_limit
    && tariff.traffic_gb === POPULAR_PRESET.traffic_gb
  ));
  const popularKey = popularTariff ? buildTariffKey(popularTariff) : null;

  const paidTariffs = sortedTariffs.filter(isPaidTariff);
  const bestCandidate = findBestByPricePerDay(paidTariffs);
  let bestKey = bestCandidate ? buildTariffKey(bestCandidate.tariff) : null;

  if (bestKey && (bestKey === cheapestKey || bestKey === popularKey)) {
    const fallbackTariffs = paidTariffs.filter((tariff) => {
      const key = buildTariffKey(tariff);
      return key !== cheapestKey && key !== popularKey;
    });

    const fallbackBest = findBestByPricePerDay(fallbackTariffs);
    bestKey = fallbackBest ? buildTariffKey(fallbackBest.tariff) : bestKey;
  }

  const orderedTariffs = [];
  const usedKeys = new Set();

  const pushUniqueByKey = (tariff) => {
    if (!tariff) return;
    const key = buildTariffKey(tariff);
    if (usedKeys.has(key)) return;
    usedKeys.add(key);
    orderedTariffs.push(tariff);
  };

  pushUniqueByKey(cheapestTariff);
  pushUniqueByKey(popularTariff);
  pushUniqueByKey(sortedTariffs.find((tariff) => buildTariffKey(tariff) === bestKey));

  sortedTariffs.forEach((tariff) => {
    if (orderedTariffs.length >= Math.min(3, sortedTariffs.length)) {
      return;
    }

    pushUniqueByKey(tariff);
  });

  const maxPricePerDay = paidTariffs.length
    ? Math.max(...paidTariffs.map((tariff) => tariff.price / tariff.duration_days))
    : 0;

  return {
    orderedTariffs,
    cheapestKey,
    popularKey,
    bestKey,
    maxPricePerDay,
  };
};
